import type { MenuItem } from '@/components/page-specific/catering/common/domain/menu-item'
import type { BasketItemFragment } from '@/generated/graphql'

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { useShallow } from 'zustand/react/shallow'

import type { ItemAddSource } from '../hooks/useMixpanelItemAddedToBasket'
import type { AddItemPayload } from '../states/menu-page/add-item-mutation'

// Operation types for tracking loading states
type CartOperation =
  | 'addingItem'
  | 'removingItem'
  | 'updatingQuantity'
  | 'updatingOptions'
  | 'creatingBasket'
  | 'savingStandingOrder'
  | 'copyingLink'

// Snackbar types
type SnackbarType = 'standingOrderSaved' | 'linkCopied' | 'itemAdded' | 'itemRemoved' | 'error'

interface CartUIState {
  // Layout State
  mobileBasketOpen: boolean
  navHeight: number

  // Preview state for navigation summary
  preview: {
    itemQuantity: number
    grandTotal: string
    newItemWasJustAdded: boolean
  }

  // Operation States - track what operations are in progress
  operations: Record<CartOperation, boolean>

  // Notification State
  snackbar: {
    type: SnackbarType | null
    message?: string
    autoHideDuration?: number
  }

  // Reorder feature state (PT-1059, behind cateringReorderMenuPage)
  reorder: {
    loadState: 'idle' | 'confirming' | 'loading' | 'success' | 'error'
    pendingReorderId: string | null
    /**
     * Order queued to load once a basket is created (no-draft Load flow).
     * Tagged with vendorId so a stale queue (e.g. user navigated to a different
     * vendor without finishing the create-basket flow) doesn't fire against the
     * wrong vendor's new basket.
     */
    queuedForNewBasket: { orderId: string; vendorId: string } | null
    lastResult: {
      loaded: number
      unavailable: number
      total: number
    } | null
  }
}

interface CartModalState {
  // Order Details Modal
  orderDetailsModal: {
    open: boolean
    createOnConfirm?: boolean
    itemToAddOnCreate?: AddItemPayload
    itemToAddSource?: ItemAddSource
  }

  /**
   * Set when the New team order button is clicked, read at basket-creation time (immediate or
   * deferred via the order-details modal) to tag the new basket isCateringTeamOrder: true. Lives
   * alongside orderDetailsModal — the same deferred-creation-intent slice — because the
   * immediate-create path never opens that modal, so the flag can't live inside it.
   */
  pendingCateringTeamOrder: boolean

  // Edit Item Modal
  editItemModal:
    | {
        type: 'displayed'
        basketItem?: BasketItemFragment
        menuItem?: MenuItem
        menuItemReference?: string
        mode: 'edit' | 'add'
      }
    | { type: 'hidden' }
}

interface CartState {
  ui: CartUIState
  modals: CartModalState
}

interface CartActions {
  // UI Actions
  setMobileBasketOpen: (open: boolean) => void
  toggleMobileBasket: () => void
  setNavHeight: (height: number) => void

  // Preview Actions
  setPreviewData: (preview: CartUIState['preview']) => void

  // Operation Actions
  setOperationState: (operation: CartOperation, loading: boolean) => void
  isOperationInProgress: (operation: CartOperation) => boolean
  withOperation: <T>(operation: CartOperation, fn: () => Promise<T>) => Promise<T>

  // Snackbar Actions
  showSnackbar: (type: SnackbarType, message?: string, autoHideDuration?: number) => void
  showLinkCopiedSnackbar: () => void
  showStandingOrderSavedSnackbar: () => void
  showItemAddedSnackbar: (itemName?: string) => void
  showItemRemovedSnackbar: (itemName?: string) => void
  showErrorSnackbar: (message: string) => void
  hideSnackbar: () => void

  // Modal Actions
  setOrderDetailsModalState: (state: {
    open: boolean
    createOnConfirm?: boolean
    itemToAddOnCreate?: AddItemPayload
    itemToAddSource?: ItemAddSource
  }) => void
  openOrderDetailsModal: (createOnConfirm?: boolean, itemToAddOnCreate?: AddItemPayload) => void
  closeOrderDetailsModal: () => void
  setPendingCateringTeamOrder: (pending: boolean) => void

  setEditItemModal: (state: CartModalState['editItemModal']) => void
  openEditItemModal: (
    basketItem?: BasketItemFragment,
    menuItem?: MenuItem,
    menuItemReference?: string,
    mode?: 'edit' | 'add',
  ) => void
  closeEditItemModal: () => void

  // Reorder Actions
  setReorderLoadState: (
    loadState: 'idle' | 'confirming' | 'loading' | 'success' | 'error',
    pendingReorderId?: string | null,
  ) => void
  setReorderLastResult: (
    result: { loaded: number; unavailable: number; total: number } | null,
  ) => void
  setQueuedForNewBasket: (queued: { orderId: string; vendorId: string } | null) => void
}

type CartStore = CartState & CartActions

const initialOperations: Record<CartOperation, boolean> = {
  addingItem: false,
  removingItem: false,
  updatingQuantity: false,
  updatingOptions: false,
  creatingBasket: false,
  savingStandingOrder: false,
  copyingLink: false,
}

export const useCartStore = create<CartStore>()(
  devtools(
    immer((set, get) => ({
      // Initial State
      ui: {
        mobileBasketOpen: false,
        navHeight: 64,
        preview: {
          itemQuantity: 0,
          grandTotal: '',
          newItemWasJustAdded: false,
        },
        operations: { ...initialOperations },
        snackbar: {
          type: null,
          message: undefined,
          autoHideDuration: 4000,
        },
        reorder: {
          loadState: 'idle',
          pendingReorderId: null,
          queuedForNewBasket: null,
          lastResult: null,
        },
      },
      modals: {
        orderDetailsModal: { open: false },
        pendingCateringTeamOrder: false,
        editItemModal: { type: 'hidden' },
      },

      // UI Actions
      setMobileBasketOpen: (open) =>
        set((state) => {
          state.ui.mobileBasketOpen = open
        }),
      toggleMobileBasket: () =>
        set((state) => {
          state.ui.mobileBasketOpen = !state.ui.mobileBasketOpen
        }),
      setNavHeight: (height) =>
        set((state) => {
          state.ui.navHeight = height
        }),

      // Preview Actions
      setPreviewData: (preview) =>
        set((state) => {
          const prev = state.ui.preview
          if (
            prev.itemQuantity === preview.itemQuantity &&
            prev.grandTotal === preview.grandTotal &&
            prev.newItemWasJustAdded === preview.newItemWasJustAdded
          ) {
            return
          }
          state.ui.preview = preview
        }),

      // Operation Actions
      setOperationState: (operation, loading) =>
        set((state) => {
          state.ui.operations[operation] = loading
        }),
      isOperationInProgress: (operation) => get().ui.operations[operation],
      withOperation: async (operation, fn) => {
        const { setOperationState } = get()
        setOperationState(operation, true)
        try {
          return await fn()
        } finally {
          setOperationState(operation, false)
        }
      },

      // Enhanced Snackbar Actions
      showSnackbar: (type, message, autoHideDuration = 4000) =>
        set((state) => {
          state.ui.snackbar = {
            type,
            message,
            autoHideDuration,
          }
        }),
      showLinkCopiedSnackbar: () =>
        set((state) => {
          state.ui.snackbar = {
            type: 'linkCopied',
            message: 'Link copied to clipboard',
            autoHideDuration: 3000,
          }
        }),
      showStandingOrderSavedSnackbar: () =>
        set((state) => {
          state.ui.snackbar = {
            type: 'standingOrderSaved',
            message: 'Standing order saved successfully',
            autoHideDuration: 4000,
          }
        }),
      showItemAddedSnackbar: (itemName) =>
        set((state) => {
          state.ui.snackbar = {
            type: 'itemAdded',
            message: itemName ? `${itemName} added to basket` : 'Item added to basket',
            autoHideDuration: 3000,
          }
        }),
      showItemRemovedSnackbar: (itemName) =>
        set((state) => {
          state.ui.snackbar = {
            type: 'itemRemoved',
            message: itemName ? `${itemName} removed from basket` : 'Item removed from basket',
            autoHideDuration: 3000,
          }
        }),
      showErrorSnackbar: (message) =>
        set((state) => {
          state.ui.snackbar = {
            type: 'error',
            message,
            autoHideDuration: 6000,
          }
        }),
      hideSnackbar: () =>
        set((state) => {
          state.ui.snackbar.type = null
          state.ui.snackbar.message = undefined
        }),

      // Modal Actions
      setOrderDetailsModalState: (modalState) =>
        set((state) => {
          const current = state.modals.orderDetailsModal
          if (
            current.open === modalState.open &&
            current.createOnConfirm === modalState.createOnConfirm &&
            current.itemToAddOnCreate === modalState.itemToAddOnCreate &&
            current.itemToAddSource === modalState.itemToAddSource
          ) {
            return
          }
          state.modals.orderDetailsModal = modalState
        }),
      openOrderDetailsModal: (createOnConfirm, itemToAddOnCreate) =>
        set((state) => {
          state.modals.orderDetailsModal = {
            open: true,
            createOnConfirm,
            itemToAddOnCreate,
          }
        }),
      closeOrderDetailsModal: () =>
        set((state) => {
          state.modals.orderDetailsModal = { open: false }
        }),
      setPendingCateringTeamOrder: (pending) =>
        set((state) => {
          state.modals.pendingCateringTeamOrder = pending
        }),

      setEditItemModal: (modalState) =>
        set((state) => {
          state.modals.editItemModal = modalState
        }),
      openEditItemModal: (basketItem, menuItem, menuItemReference, mode = 'edit') =>
        set((state) => {
          state.modals.editItemModal = {
            type: 'displayed',
            basketItem,
            menuItem,
            menuItemReference,
            mode,
          }
        }),
      closeEditItemModal: () =>
        set((state) => {
          state.modals.editItemModal = { type: 'hidden' }
        }),

      setReorderLoadState: (loadState, pendingReorderId) =>
        set((state) => {
          state.ui.reorder.loadState = loadState
          if (pendingReorderId !== undefined) {
            state.ui.reorder.pendingReorderId = pendingReorderId
          }
        }),
      setReorderLastResult: (lastResult) =>
        set((state) => {
          state.ui.reorder.lastResult = lastResult
        }),
      setQueuedForNewBasket: (queued) =>
        set((state) => {
          state.ui.reorder.queuedForNewBasket = queued
        }),
    })),
    {
      name: 'cart-store',
    },
  ),
)

// Convenience hooks for specific store sections
export const useCartUI = () => useCartStore((state) => state.ui)
export const useCartModals = () => useCartStore((state) => state.modals)
export const useCartOperations = () => useCartStore((state) => state.ui.operations)
export const useCartSnackbar = () => useCartStore((state) => state.ui.snackbar)
export const useCartPreview = () => useCartStore((state) => state.ui.preview)
export const useCartPreviewActions = () =>
  useCartStore(
    useShallow((state) => ({
      setPreviewData: state.setPreviewData,
    })),
  )

// Convenience hooks for specific actions
export const useCartUIActions = () =>
  useCartStore(
    useShallow((state) => ({
      setMobileBasketOpen: state.setMobileBasketOpen,
      toggleMobileBasket: state.toggleMobileBasket,
      setNavHeight: state.setNavHeight,
    })),
  )

export const useCartOperationActions = () =>
  useCartStore(
    useShallow((state) => ({
      setOperationState: state.setOperationState,
      isOperationInProgress: state.isOperationInProgress,
      withOperation: state.withOperation,
    })),
  )

export const useCartSnackbarActions = () =>
  useCartStore(
    useShallow((state) => ({
      showSnackbar: state.showSnackbar,
      showLinkCopiedSnackbar: state.showLinkCopiedSnackbar,
      showStandingOrderSavedSnackbar: state.showStandingOrderSavedSnackbar,
      showItemAddedSnackbar: state.showItemAddedSnackbar,
      showItemRemovedSnackbar: state.showItemRemovedSnackbar,
      showErrorSnackbar: state.showErrorSnackbar,
      hideSnackbar: state.hideSnackbar,
    })),
  )

export const useCartModalActions = () =>
  useCartStore(
    useShallow((state) => ({
      setOrderDetailsModalState: state.setOrderDetailsModalState,
      openOrderDetailsModal: state.openOrderDetailsModal,
      closeOrderDetailsModal: state.closeOrderDetailsModal,
      setPendingCateringTeamOrder: state.setPendingCateringTeamOrder,
      setEditItemModal: state.setEditItemModal,
      openEditItemModal: state.openEditItemModal,
      closeEditItemModal: state.closeEditItemModal,
    })),
  )

export const useCartReorder = () => useCartStore((state) => state.ui.reorder)

export const useCartReorderActions = () =>
  useCartStore(
    useShallow((state) => ({
      setReorderLoadState: state.setReorderLoadState,
      setReorderLastResult: state.setReorderLastResult,
      setQueuedForNewBasket: state.setQueuedForNewBasket,
    })),
  )
